import React, { useState, useEffect, useRef } from 'react';
import { renderVariables } from '../utils/variableRenderer';

const FooterElement = ({ 
  blockId, 
  config, 
  style, 
  isEditing, 
  onToggleEditMode, 
  onUpdateCustomData, 
  customData, 
  onConfigUpdate, 
  organizationName, 
  scanDate, 
  mspInfo, 
  clientInfo, 
  selectedBlock, 
  onSelect,
  children
}) => {
  const [editContent, setEditContent] = useState('');
  
  // Check layout type
  const layoutType = config.layout?.type || 'legacy';
  const isFreeform = layoutType === 'freeform' || (children && children.length > 0);
  const hasColumnLayout = layoutType === 'columns';

  // Legacy single content edit mode
  useEffect(() => {
    if (isEditing && !hasColumnLayout && !isFreeform) {
      const displayContent = customData.content !== undefined ? customData.content : config.content;
      setEditContent(displayContent || 'Footer');
    }
  }, [isEditing, config.content, customData, hasColumnLayout, isFreeform]);

  const handleSave = () => {
    onUpdateCustomData({ content: editContent });
    if (onConfigUpdate) onConfigUpdate({ content: editContent });
    onToggleEditMode();
  };

  const renderContent = (content) => {
    if (!content) return null;
    const lines = content.split('\n');
    const result = [];
    lines.forEach((line, index) => {
      const lineContent = renderVariables(line, organizationName, scanDate, mspInfo, clientInfo);
      result.push(<span key={`line-${index}`} style={{ display: 'inline' }}>{lineContent}</span>);
      if (index < lines.length - 1) result.push(<br key={`br-${index}`} />);
    });
    return result;
  };

  const displayContent = customData.content !== undefined ? customData.content : config.content;

  // Edit mode for legacy content
  if (isEditing && !hasColumnLayout && !isFreeform) {
    return (
      <div className="footer-element editing" style={{ position: 'relative', minHeight: '100%' }}>
        <div className="edit-controls">
          <div className="edit-field">
            <label>Footer Content:</label>
            <textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              rows="3"
              style={{ width: '100%', marginBottom: '8px' }}
            />
          </div>
          <div className="edit-actions">
            <button onClick={handleSave} className="btn btn-sm btn-success">Save</button>
            <button onClick={onToggleEditMode} className="btn btn-sm btn-secondary">Cancel</button>
          </div>
        </div>
        {/* Still render children in edit mode to avoid flickering/loss of visibility */}
        <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 2, pointerEvents: 'none' }}>
           <div style={{ position: 'relative', width: '100%', height: '100%', pointerEvents: 'auto' }}>
              {children}
           </div>
        </div>
      </div>
    );
  }

  // Column layout (legacy structured)
  if (hasColumnLayout) {
    return (
      <div className="footer-element column-layout" style={{ position: 'relative', display: 'flex', width: '100%', height: '100%' }}>
         <div style={{ display: 'flex', width: '100%', height: '100%', zIndex: 1 }}>
           {config.layout.columns?.map(col => (
             <div key={col.id} style={{ flex: col.flex || 1, padding: '8px' }}>
               {col.elements?.map(el => <div key={el.id}>{renderContent(el.content)}</div>)}
             </div>
           ))}
         </div>
         <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 2, pointerEvents: 'none' }}>
            <div style={{ position: 'relative', width: '100%', height: '100%', pointerEvents: 'auto' }}>
               {children}
            </div>
         </div>
      </div>
    );
  }

  // Freeform or Legacy Default
  return (
    <div
      className={`footer-element ${isFreeform ? 'freeform' : ''}`}
      style={{
        height: '100%',
        width: '100%',
        display: isFreeform ? 'block' : 'flex',
        flexDirection: isFreeform ? undefined : 'column',
        justifyContent: isFreeform ? undefined : 'center', // Default for legacy
        textAlign: config.align || 'left',
        position: 'relative', // Ensure children are positioned relative to this container
        pointerEvents: 'auto', // Re-enable pointer events for children
        overflow: 'visible', // CRITICAL: Allow children (like resize handles) to overflow
        isolation: 'isolate' // Create new stacking context
      }}
    >
      <div style={{ position: 'relative', width: '100%', zIndex: 1 }}>
        {renderContent(displayContent)}
      </div>
      <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 2, pointerEvents: 'none' }}>
         <div style={{ position: 'relative', width: '100%', height: '100%', pointerEvents: 'auto' }}>
            {children}
         </div>
      </div>
    </div>
  );
};

export default FooterElement;
